/**
 * The rail's two groups, built from the contract kit (GDN-3381).
 *
 * The keys, labels, colors and aliases live in packet D's
 * `types/encounterWork.ts`, which is the shared reading of the contract. What
 * belongs HERE is the one thing the rail adds: which group each key sits in,
 * and the total resolver a URL needs.
 *
 * The grouping is the product's promise made visible. A QUEUE is work someone
 * still owes an answer on, so it honours archive and snooze; a LIST is a lens
 * over the same claims and never hides anything.
 */
import {
  LIST_VIEW_KEYS,
  QUEUE_VIEW_KEYS,
  WORK_VIEW_ALIASES,
  WORK_VIEW_KEYS,
  WORK_VIEW_LABELS,
  WORK_VIEW_RAIL_COLORS,
  type WorkViewAliasKey,
  type WorkViewKey,
} from '../../../types/encounterWork';

export const DEFAULT_CLAIM_WORK_VIEW: WorkViewKey = 'all_claim_work';

export interface ClaimWorkViewDef {
  key: WorkViewKey;
  label: string;
  color: string;
}

const viewDef = (key: WorkViewKey): ClaimWorkViewDef => ({
  key,
  label: WORK_VIEW_LABELS[key],
  color: WORK_VIEW_RAIL_COLORS[key],
});

export const CLAIM_WORK_VIEW_GROUPS: Array<{ label: string; views: ClaimWorkViewDef[] }> = [
  { label: 'Queues', views: QUEUE_VIEW_KEYS.map(viewDef) },
  { label: 'Lists', views: LIST_VIEW_KEYS.map(viewDef) },
];

/**
 * A key as a URL or a saved view STORES it: the seven canonical keys plus the
 * two the server still accepts.
 *
 * The distinction matters more than it looks. `needs_correction` is not a
 * synonym for `fix` on the server: it is the union of review and fix, its
 * old meaning, kept so saved views built on it keep their rows. So the
 * stored key is what goes to the server, unchanged, and the rail key below
 * is only ever used to decide which item to light up.
 */
export type StoredClaimWorkViewKey = WorkViewKey | WorkViewAliasKey;

const KNOWN_KEYS = new Set<string>(WORK_VIEW_KEYS);
const ALIAS_KEYS = new Set<string>(Object.keys(WORK_VIEW_ALIASES));

export const isStoredClaimWorkViewKey = (
  value: string | null | undefined,
): value is StoredClaimWorkViewKey =>
  Boolean(value) && (KNOWN_KEYS.has(value as string) || ALIAS_KEYS.has(value as string));

/**
 * Any stored or shared view key to a rail key. Total, unlike the kit's
 * `resolveWorkViewKey`, because a URL hands us an arbitrary string: the seven
 * canonical keys pass through, the two aliases map, everything else is null
 * and the caller picks its own default.
 */
export function resolveClaimWorkViewKey(
  value: string | null | undefined,
): WorkViewKey | null {
  if (!value) return null;
  if (KNOWN_KEYS.has(value)) return value as WorkViewKey;
  return WORK_VIEW_ALIASES[value as WorkViewAliasKey] ?? null;
}

export const claimWorkViewLabel = (key: WorkViewKey): string => WORK_VIEW_LABELS[key];

/**
 * The same view, named for a single row. Four of the seven labels count
 * their subject ("Claims to fix"), and a queue that holds one claim has to
 * say so: "1 claims to fix" is the kind of wrongness a biller reads as the
 * screen not being finished. The three that name a state rather than a
 * count are the same word either way.
 */
const SINGULAR_VIEW_LABELS: Record<WorkViewKey, string> = {
  all_claim_work: 'All claim work',
  awaiting_payer: 'Awaiting payer',
  fix: 'Claim to fix',
  payer_in_progress: 'With payer',
  payment_posted: 'Paid',
  review: 'Claim to review',
  submit: 'Claim to submit',
};

export const claimWorkViewLabelForCount = (key: WorkViewKey, count: number): string =>
  count === 1 ? SINGULAR_VIEW_LABELS[key] : WORK_VIEW_LABELS[key];
